import type { UseCreateOptions } from "./useCreate.type";
import type { ApiError, ApiPayloadCreate } from "@/services/api";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { client } from "@/config/client";

export const useCreate = <Data = unknown, Variables = unknown>(
  model: string,
  options?: UseCreateOptions<Data, Variables>
) => {
  const queryClient = useQueryClient();

  const { rootId, invalidateKeys, onSuccess, mutationOptions } = options ?? {};

  const mutationFn = (variables: Variables) => {
    const [modelRoot, nestedModel] = model.split(".");
    const clientBase =
      modelRoot && nestedModel ? client[modelRoot][nestedModel] : client[model];
    return rootId
      ? clientBase.create(rootId, variables)
      : clientBase.create(variables);
  };

  const customOnSuccess = (response: ApiPayloadCreate<Data>) => {
    if (onSuccess) onSuccess?.(response.data.data);
    queryClient.invalidateQueries({ queryKey: [model, "list"] });
    invalidateKeys?.forEach((queryKey) =>
      queryClient.invalidateQueries({ queryKey })
    );
  };

  return useMutation<ApiPayloadCreate<Data>, ApiError, Variables>({
    ...mutationOptions,
    mutationFn,
    onSuccess: customOnSuccess,
  });
};
